import { Injectable } from '@angular/core';
import { User } from '@mitel/cloudlink-sdk';
import { AppInjector } from '../../app-injector';
import { productLicenses } from '../constants/constants';
import { ProductLicense, ProductLicenseItemServiceInf } from '../interfaces/product-licenses-interfaces';
import { OrdersService } from './orders.service';
import { AdminService } from './admin.service';
import { AccountsService } from './accounts.service';
import { CustomValidators } from '../utils/CustomValidators';
import { IntegPolicyService } from './integ-policy.service';
import { DisableMeetingsAccountAccessStatement } from '../constants/productPolicies';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';
import { Util } from '../utils/Util';

@Injectable({
  providedIn: 'root'
})
export class UserProdMiteamMeetingsService implements ProductLicenseItemServiceInf {

  private ordersService: OrdersService;
  private accountService: AccountsService;
  private adminService: AdminService;
  private policyService: IntegPolicyService;
  private rbacAuthGuardService: RBACAuthGuardService;

  constructor() { }

  initializeServices()
	{
    if ( !this.ordersService)
      this.ordersService = AppInjector.injector.get(OrdersService);

    if ( !this.accountService )
      this.accountService = AppInjector.injector.get(AccountsService);

    if ( !this.adminService )
      this.adminService = AppInjector.injector.get(AdminService);

    if ( !this.policyService )
			this.policyService = AppInjector.injector.get(IntegPolicyService);

    if (!this.rbacAuthGuardService )
      this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);
  }
  
  canBeAdded(user: User, product: ProductLicense) {
    this.initializeServices();
    let promises = [];
    if ( user.userVerified != true)
    {
      if (user.email && user.email.length > 0) {
        let dupEmailPromise =  this.adminService.userHasADuplicateEmail(user.accountId, user).then(hasDup => {
                                  const validEmail = CustomValidators.userHasValidEmail(user);
                                  if ( hasDup === true || !validEmail)
                                  {
                                    return false;
                                  }
                                  return true;
                                });
        promises.push(dupEmailPromise);
      } else {
        // if user is verified but doesn't have an email then we allow it to get a license
        // User from MiCloud Connect are verified but they may have invalid email or no email. email is not validated. 
        product.statusString = 'users_edit_page.user_not_eligible';
        return Promise.resolve(false);
      }
    } else {
      promises.push(Promise.resolve(true));
    }

    let remainingLicense = this.getRemainingLicenses(user).then( value => {
                                if (value > 0)
                                  return true;
                                return false;
                            });
    promises.push(remainingLicense);
    
    let accountTagPromise = this.accountService.getAccountTagsById(user.accountId).then((tags: any) => {
                                if (tags.products && tags.products.includes('MiCollab')) {  
                                  return false;
                                } 
                                return true;
                              })
                              .catch (err => {
                                console.error("There was an error retrieving the tags so no list of products", err);
                                // do not think this is a issue that warrants an error message as there is nothing the user can do to remedy it
                                return false;
                              });
    promises.push(accountTagPromise);


    return Promise.all(promises).then( result => {
              if (result[0] && result[1] && result[2] )
                  return true;

              if (!result[2])
                product.statusString = 'users_edit_page.assignUsingMicollab';
              // I am going to leave it here for now in case they tell us to block the addition of Meetings if Mitel one is
              // added.
              // else if (!result[2])
              //   product.statusString = 'product_licenses.meet_added_with_mitelOne';
              else if (!result[1])
                product.statusString = 'users_edit_page.licensesNotAvailable';
              else if (!result[0])
                product.statusString = 'users_edit_page.user_not_eligible';

              return false;
            });
  }

  hasRequiredAccountIntegrations( user: User, accountIntegrations: string[] ):  Promise<boolean>
  {
    return Promise.resolve( accountIntegrations.includes('MiTeam') );
  }

  getConfigurationDialog(setupComplete?: boolean): Promise<any> {
    return Promise.resolve(undefined);
  }

  isSetupComplete(user: User): Promise<boolean> {
    return Promise.resolve(true);
  }

  handleProductStateChange(user: User, state: boolean, isSubComponent?: boolean): Promise<any> {
    this.initializeServices();
    console.log('handling state change of miteam meetings product for the user');
    if (isSubComponent) {
      return this.adminService.getUserWithoutFrills(user.userId).then( newUser => {
                if ( state == true )
                {
                  // remove the user policy statement
                  if ( newUser['policyId'] === undefined ) // nothing to remove
                  {
                    return Promise.resolve(true);
                  }
                  return this.adminService.getPolicy(user.accountId, newUser['policyId']).then( policy => {
                      console.log("Here are the user policies", policy);
                      const foundPolicy = policy.statements.find(stmt => {
                        return stmt.statementId === DisableMeetingsAccountAccessStatement.statementId;
                      });
                      if ( foundPolicy )
                      {
                        return this.policyService.removeStatementFromPolicy(newUser, policy, DisableMeetingsAccountAccessStatement);
                      }
                      else
                        return Promise.resolve(true);
                  });
          
                }
                else
                {
                  // add the policy statement for the user
                  return this.policyService.addStatementToPolicy( newUser, DisableMeetingsAccountAccessStatement).then( policy => {
                    if ( newUser['policyId'] == undefined ) {
                      let dataChanges = {
                        policyId: policy.policyId,
                      }
                      return this.adminService.updateUserByAccountIdWithoutSpinnerAndNotify( newUser.accountId, newUser.userId,dataChanges);
                    }
                    else
                      return policy;
                  }).catch(err => {
                    console.log("Failed to add/update policy", err);
                    throw err;
                  });
                }
      });
    } else {
      return this.ordersService.addOrRemoveUserLicense(user.userId, productLicenses['MiTeam Meetings'] as any, state)
            .then(value => {
              this.ordersService.clearAcctSubscriptionsCache();
              this.postStateChangeProcessing(user, state)
                    .catch(emailError => {
                      console.log("There was an arror while sending welcome email", emailError);
                    });
            })
            .catch( err => {
              console.log('Failed to update the meetings license for user', user);
            });

    }
  }

  private postStateChangeProcessing( user: User, state: boolean ): Promise<any>
  {
    if ( state === true )
    {
        if ( user.userVerified != true )
        {
          console.log("User is not verified, so we will send a welcome email for the new Meetings license", user.userId);
          // send an email if user is not verified
          let emailBody = { "application": "meetings" };
          return this.adminService.sendWelcomeEmailWithoutNotify(user.accountId, user.userId, emailBody).then(done => {
            console.log("email sent for the new meetings license", user.userId);
            return done;
          });
        }
        else // user was verified
        {
          return this.adminService.sendMiteamMeetingOnlyWelcomeEmail(user).then(done => {
            console.log("email sent for the new Meetings license", user.userId);
            return done;
          });
        }
    }
    return Promise.resolve();
  }

  isButtonDisabled(user: User, product: ProductLicense, isSubComponent?: boolean): Promise<boolean> {
    this.initializeServices();
    if (isSubComponent) {
      return this.adminService.getAccountWithoutFrills(user.accountId).then( newAccount => {
			
                // remove the account policy statement
                if ( newAccount['policyId'] === undefined ) // nothing to remove
                {
                  return false;
                }
                return this.adminService.getPolicy(newAccount.accountId, newAccount['policyId']).then( policy => {
                  console.log("Here are the account policies", policy);
                  const foundPolicy = policy.statements.find(stmt => {
                    return stmt.statementId === DisableMeetingsAccountAccessStatement.statementId;
                  });
                  if ( foundPolicy )
                  {
                    product.statusString = "product_licenses.account-wide-meet-disable";
                    return true;
                  }
                  else
                    return false;
                });
      });
    }
    // For standalone Meetings as a product
    if(Util.isReadOnlyMode(user.source))
      return Promise.resolve(true);

    let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.USERSLICENSE, ActionType.DELETE);

    if(!isDenied){
      return this.accountService.getAccountTagsById(user.accountId).then((tags: any) => {
          if (tags.products && (tags.products.includes('MiCollab'))) {
            return true;
          } 
          return false;
      })
      .catch (err => {
        console.error("There was an error retrieving the tags so no list of products", err);
        // do not think this is a issue that warrants an error message as there is nothing the user can do to remedy it
        // disable the button
        return true;
      });
    }else{
      return Promise.resolve(isDenied);
    }
  }


  isProductEnabled(user: User, isSubComponent?): Promise<boolean> {
    this.initializeServices();
    
    if (isSubComponent) {
      if(!user['policyId']) {
        return Promise.resolve(true);
      }
      return this.adminService.getPolicy(user.accountId, user['policyId']).then( policy => {
          console.log("Here are the user policies", policy);
          const foundPolicy = policy.statements.find(stmt => {
            return stmt.statementId === DisableMeetingsAccountAccessStatement.statementId;
          });
          if ( foundPolicy )
          {
            return false;
          } else
            return true;
      });
    } else {
      // for standalone meetings as a product
      return this.ordersService.getUserLicenses(user.userId)
              .then((userLicenses) => {
                const result = userLicenses.find( element => {
                  return element.enable && element.name === productLicenses["MiTeam Meetings"];
                });
                return !!result;
              })
              .catch( (err) => {
                console.log('Failed to get user Licenses', err);
                return false;
              });
    }
    
  }

  getRemainingLicenses(user: User, isSubComponent?: boolean): Promise<number> {
    this.initializeServices();
    if (isSubComponent) {
      // Hard coding here because there are no separate license for meetings when it is a feature in mitel one. 
      // But, product and license section has been designed to generalize this behaviour to accomodate the scenario
      // where if feature can only be enabled if license is there. 
      return Promise.resolve(1000);
    }
    return this.ordersService.getRemainingLicenses().then((remainingLicenses) => {
                                        console.log("Remaining licenses are", remainingLicenses);
                                        return remainingLicenses["MiTeam Meetings"] ? remainingLicenses["MiTeam Meetings"] : 0;
                                      })
                                      .catch( err => {
                                        console.log('Failed to get the remaining licenses', err);
                                        return 0;
                                      }); 
  }
  
}
